"use client";
import React, { useRef, useState } from "react";
import {
  CompositeDecorator,
  ContentState,
  DraftHandleValue,
  EditorState,
  RichUtils,
} from "draft-js";
import "draft-js/dist/Draft.css";
import { Box } from "@mui/material";
import { Editor as EditorComponent } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import { getPlainText, stripHTML } from "../../../utils/helpers";

interface IRichtextInput {
  value?: string;
  placeholder?: string;
  maxLength?: number;
  onChange: (val: any, tags?: string[]) => void;
}

const findHashtags = (contentBlock: any, callback: any) => {
  const text = contentBlock.getText();
  const regex = /#[\w]+/g;
  let matchArr;
  while ((matchArr = regex.exec(text)) !== null) {
    callback(matchArr.index, matchArr.index + matchArr[0].length);
  }
};

const HashtagSpan = (props: any) => {
  return <span style={{ color: "#c80000" }}>{props.children}</span>;
};

function TextEditor({
  value = "",
  placeholder,
  maxLength = 500,
  onChange,
}: IRichtextInput) {
  const decorator = new CompositeDecorator([
    {
      strategy: findHashtags,
      component: HashtagSpan,
    },
  ]);
  const [editorState, setEditorState] = useState(() => {
    let contentState;
    if (value.startsWith("<")) {
      // If the initialState is HTML, convert it using stateFromHTML
      contentState = stateFromHTML(value);
    } else {
      // Otherwise, treat it as plaintext and convert it to DraftJS content
      contentState = ContentState.createFromText(value);
    }
    return EditorState.createWithContent(contentState, decorator);
  });
  const editorWrapperRef = useRef<HTMLDivElement>(null);

  const handleEditorChange = (state: EditorState) => {
    setEditorState(state);
    if (onChange) {
      // get hashtag
      const plainText = state.getCurrentContent().getPlainText();
      const hashtags = (plainText.match(/#[\w]+/g) || []).map(
        (tag) => tag.slice(1) // Remove the `#`
      );
      const htmlContent = stateToHTML(state.getCurrentContent());
      onChange(htmlContent, hashtags);
    }
  };

  const handleKeyCommand = (command: string): DraftHandleValue => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return "handled";
    }
    return "not-handled";
  };

  const handleBeforeInput = (
    chars: string,
    state: EditorState
  ): DraftHandleValue => {
    const content = getPlainText(
      stripHTML(stateToHTML(state.getCurrentContent())).trim()
    );

    if (content.length + chars.length > maxLength) {
      return "handled"; // Prevent input
    }
    return "not-handled"; // Allow input
  };

  const handlePastedText = (
    pastedText: string,
    html: string | undefined,
    state: EditorState
  ): DraftHandleValue => {
    const content = getPlainText(
      stripHTML(stateToHTML(state.getCurrentContent())).trim()
    );

    if (content?.length + getPlainText(pastedText).length > maxLength) {
      return "handled"; // Prevent pasting
    }
    return "not-handled"; // Allow pasting
  };

  return (
    <Box
      ref={editorWrapperRef}
      sx={{
        "& .public-DraftEditor-content": { padding: 0 },
      }}
    >
      <EditorComponent
        placeholder={placeholder}
        preserveSelectionOnBlur
        editorState={editorState}
        onChange={handleEditorChange}
        handleBeforeInput={handleBeforeInput}
        handlePastedText={handlePastedText}
        handleKeyCommand={handleKeyCommand}
      />
    </Box>
  );
}

export default TextEditor;
